import React, { useEffect, useState } from 'react';
import { webNavigate } from '../../services/navigate.service';
import { createCustomerOrder } from '../../services/customerOrders.service';
import { getListProduct } from '../../services/items.services';
import { getLocations } from '../../services/inventoryCorrections.service';
import { useResponsive } from '../../hook/useResponsive';
import { describeApiError } from '../../services/apiError';
import { inventoryFormLimits, limitProps, useFieldLimits, validateAgainstLimits } from '../../services/fieldLimits';
import FieldCounter from '../../components/FieldCounter';

const TEXT = '#222525';
const MUTED = '#4A5353';
const TEAL = '#0E5566';
const DANGER = '#B3261E';

// The host shell owns the bottom edge on mobile: it defines --finex-tabbar-h on
// :root, and an older shell without it falls back to 0px (see ListMobile).
const TABBAR_OFFSET = 'var(--finex-tabbar-h, 0px)';

interface DraftLine {
  productId: string;
  quantity: number;
}

const asArray = (value: unknown): any[] => (Array.isArray(value) ? value : []);

const inputStyle: React.CSSProperties = {
  width: '100%',
  height: '48px',
  padding: '0 14px',
  borderRadius: '10px',
  border: '1px solid #D5DEDE',
  fontSize: '14px',
};

const CustomerOrderCreate = () => {
  const { isMobile } = useResponsive();
  const [language, setLanguage] = useState<any>(null);
  const companyId = localStorage.getItem('companyId');
  const userId = localStorage.getItem('userId');
  const [locations, setLocations] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [locationId, setLocationId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [requestedFor, setRequestedFor] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([
    { productId: '', quantity: 1 },
  ]);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const limits = useFieldLimits(inventoryFormLimits('customer-order'));

  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);

  // The locale this render shows, so an error message matches the copy around it.
  const locale = localStorage.getItem('language') || 'en';
  const t = (key: string) =>
    language && language?.[key] ? language?.[key] : key;

  useEffect(() => {
    const loadPickers = async () => {
      try {
        const locationsResponse: any = await getLocations(
          userId,
          companyId,
          1,
          100,
          '',
        );
        setLocations(
          asArray(
            locationsResponse?.data?.data?.locations ??
              locationsResponse?.data?.data,
          ),
        );
        const productsResponse: any = await getListProduct(
          1,
          200,
          '',
          companyId,
        );
        setProducts(
          asArray(
            productsResponse?.data?.data?.products ??
              productsResponse?.data?.data,
          ),
        );
      } catch (loadError) {
        console.error('Error loading pickers:', loadError);
      }
    };
    loadPickers();
  }, []);

  const setLine = (index: number, patch: Partial<DraftLine>) =>
    setLines((current) =>
      current.map((line, i) => (i === index ? { ...line, ...patch } : line)),
    );

  const submit = async () => {
    setError(null);
    const chosen = lines.filter((line) => line.productId && line.quantity >= 1);
    if (!locationId || !customerName.trim() || !chosen.length) {
      setError(t('Choose a location, a customer name and at least one item.'));
      return;
    }
    const limitErrors = validateAgainstLimits(
      { customerName: customerName.trim(), customerPhone: customerPhone.trim() },
      limits,
      language,
      ['customerName', 'customerPhone'],
    );
    setFieldErrors(limitErrors);
    if (Object.keys(limitErrors).length > 0) return;
    setBusy(true);
    try {
      // Ids and quantities only — the server prices and returns the totals.
      const response: any = await createCustomerOrder({
        locationId,
        customerName: customerName.trim(),
        customerPhone: customerPhone.trim() || undefined,
        requestedFor: requestedFor
          ? new Date(requestedFor).toISOString()
          : undefined,
        notes: notes.trim() || undefined,
        lines: chosen.map((line) => ({
          productId: line.productId,
          quantity: Math.trunc(line.quantity),
        })),
      });
      const created = response?.data?.data;
      webNavigate(
        created?.id ? `/customerOrders/${created.id}` : '/customerOrders',
      );
    } catch (submitError: any) {
      // Shown inline like the form's own check; the per-field text goes under each input.
      const described = describeApiError(submitError, { language, locale });
      setFieldErrors(described.fieldErrors ?? {});
      setError(described.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="h-full relative"
      style={{ overflowY: 'auto', paddingBottom: '96px' }}
    >
      <button
        className="font-inter"
        style={{ color: TEAL, fontSize: '14px' }}
        onClick={() => webNavigate('/customerOrders')}
      >
        ← {t('Customer Orders')}
      </button>
      <p
        className="font-inter"
        style={{
          fontSize: '24px',
          fontWeight: 500,
          color: TEXT,
          marginTop: '12px',
        }}
      >
        {t('New Customer Order')}
      </p>

      <div
        className="lg:grid lg:grid-cols-2"
        style={{ gap: '20px', marginTop: '16px' }}
      >
        <div className="space-y-3">
          <div>
            <p
              className="font-inter"
              style={{ fontSize: '13px', color: MUTED, marginBottom: '6px' }}
            >
              {t('Location')}
            </p>
            <select
              className="font-inter"
              style={inputStyle}
              value={locationId}
              onChange={(event) => setLocationId(event.target.value)}
            >
              <option value="">{t('Select location')}</option>
              {locations.map((location: any) => (
                <option key={location?.id} value={location?.id}>
                  {location?.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <p
              className="font-inter"
              style={{ fontSize: '13px', color: MUTED, marginBottom: '6px' }}
            >
              {t('Customer Name')}
            </p>
            <input
              className="font-inter"
              style={inputStyle}
              {...limitProps(limits, 'customerName')}
              value={customerName}
              onChange={(event) => setCustomerName(event.target.value)}
            />
            <FieldCounter value={customerName} max={limits.customerName?.maxLength} />
            {fieldErrors.customerName && <p className="error">{fieldErrors.customerName}</p>}
          </div>
          <div>
            <p
              className="font-inter"
              style={{ fontSize: '13px', color: MUTED, marginBottom: '6px' }}
            >
              {t('Phone')}
            </p>
            <input
              className="font-inter"
              style={inputStyle}
              {...limitProps(limits, 'customerPhone')}
              value={customerPhone}
              onChange={(event) => setCustomerPhone(event.target.value)}
            />
            <FieldCounter value={customerPhone} max={limits.customerPhone?.maxLength} />
            {fieldErrors.customerPhone && <p className="error">{fieldErrors.customerPhone}</p>}
          </div>
          <div>
            <p
              className="font-inter"
              style={{ fontSize: '13px', color: MUTED, marginBottom: '6px' }}
            >
              {t('Requested For')}
            </p>
            <input
              type="datetime-local"
              className="font-inter"
              style={inputStyle}
              value={requestedFor}
              onChange={(event) => setRequestedFor(event.target.value)}
            />
          </div>
          <div>
            <p
              className="font-inter"
              style={{ fontSize: '13px', color: MUTED, marginBottom: '6px' }}
            >
              {t('Notes')}
            </p>
            <textarea
              className="font-inter"
              style={{
                ...inputStyle,
                height: 'auto',
                minHeight: '72px',
                padding: '12px 14px',
              }}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </div>
        </div>

        <div style={{ marginTop: '16px' }} className="lg:!mt-0">
          <p
            className="font-inter"
            style={{
              fontSize: '15px',
              fontWeight: 600,
              color: TEXT,
              marginBottom: '8px',
            }}
          >
            {t('Order lines')}
          </p>
          {lines.map((line, index) => (
            <div
              key={index}
              className="flex items-center"
              style={{ gap: '8px', marginBottom: '8px' }}
            >
              <select
                className="font-inter flex-1"
                style={inputStyle}
                value={line.productId}
                onChange={(event) =>
                  setLine(index, { productId: event.target.value })
                }
              >
                <option value="">{t('Add Item')}</option>
                {products.map((product: any) => (
                  <option key={product?.id} value={product?.id}>
                    {product?.productName ?? product?.name}
                  </option>
                ))}
              </select>
              <input
                type="number"
                min={1}
                step={1}
                className="font-inter"
                style={{ ...inputStyle, width: '84px', textAlign: 'center' }}
                value={line.quantity}
                onChange={(event) =>
                  setLine(index, {
                    quantity: Math.max(
                      1,
                      Math.trunc(Number(event.target.value) || 1),
                    ),
                  })
                }
              />
              <button
                className="font-inter"
                style={{ color: DANGER, fontSize: '18px', padding: '0 6px' }}
                onClick={() =>
                  setLines((current) =>
                    current.length > 1
                      ? current.filter((_, i) => i !== index)
                      : current,
                  )
                }
              >
                ×
              </button>
            </div>
          ))}
          <button
            className="font-inter"
            style={{ color: TEAL, fontSize: '14px', fontWeight: 500 }}
            onClick={() =>
              setLines((current) => [
                ...current,
                { productId: '', quantity: 1 },
              ])
            }
          >
            + {t('Add Item')}
          </button>
          <p
            className="font-inter"
            style={{ fontSize: '12px', color: MUTED, marginTop: '10px' }}
          >
            {t(
              'Prices and totals are calculated by the server when the order is created.',
            )}
          </p>
        </div>
      </div>

      {error && (
        <p
          className="font-inter"
          style={{ color: DANGER, fontSize: '14px', marginTop: '14px' }}
        >
          {error}
        </p>
      )}

      {/* On a phone the bar sits ON TOP of the host tab bar (z-30) instead of under it.
          Inline because the host Tailwind build never emits this module's lg: classes. */}
      <div
        style={{
          padding: '16px',
          background: '#FFF',
          borderTop: '1px solid #EBF0F0',
          marginTop: '16px',
          position: isMobile ? 'fixed' : undefined,
          left: isMobile ? 0 : undefined,
          right: isMobile ? 0 : undefined,
          bottom: isMobile ? TABBAR_OFFSET : undefined,
          zIndex: isMobile ? 35 : undefined,
        }}
      >
        <button
          className="font-inter"
          style={{
            width: isMobile ? '100%' : 'auto',
            background: TEAL,
            color: '#FFF',
            borderRadius: '10px',
            padding: '14px 24px',
            fontSize: '15px',
            fontWeight: 500,
            opacity: busy ? 0.6 : 1,
          }}
          disabled={busy}
          onClick={submit}
        >
          {t('Create Customer Order')}
        </button>
      </div>
    </div>
  );
};

export default CustomerOrderCreate;
